import { FontColorsOutlined, SearchOutlined } from "@ant-design/icons";
import Input, { InputProps } from "antd/lib/input";
import { useMemo } from "react";

import { useLoc, useDebouncedCallback } from "@app/shared/lib/hooks";
import { TimeSpan } from "@app/shared/lib/TimeSpan";

type InputSearchSettings = {
  match: {
    enabled: boolean;
    value: boolean;
    onChange: (value: boolean) => void;
  };
};

export type InputSearchProps = InputProps & {
  debounce?: number;
  onSearch?: (value: string) => void;
  width?: number | string;
  searchIcon?: React.ReactNode;
  iconInSuffux?: boolean;
  debounceSettings?: {
    trailing?: boolean;
    leading?: boolean;
    maxWait?: number;
  };
  /**
   * @example match setting
   * match: {
   *    enabled: true,
   *    onChange: match => filter.update({match}),
   *    value: filter.match
   * }
   */
  settings?: InputSearchSettings;
};

export const InputSearch: React.FC<InputSearchProps> = ({
  debounce = 300,
  onSearch,
  width = 250,
  searchIcon = <SearchOutlined />,
  iconInSuffux = false,
  settings,
  debounceSettings,
  allowClear = true,
  ...rest
}) => {
  const loc = useLoc();

  const search = useDebouncedCallback(
    (searchStr: string) => {
      onSearch?.(searchStr);
    },
    [],
    TimeSpan.fromMilliseconds(debounce ?? 300),
    { ...debounceSettings },
  );

  const suffix = useMemo(() => {
    const suffixArr = [];
    if (settings?.match.enabled === true)
      suffixArr.push(
        <FontColorsOutlined
          title={loc.word("InputSearch.matchWholeWordHelp", {
            default: "Match whole word",
          })}
          onClick={() => settings.match.onChange(!settings.match.value)}
          className={
            settings.match.value === true ? "color-primary" : "color-secondary"
          }
        />,
      );

    if (iconInSuffux === true) suffixArr.push(searchIcon);

    return suffixArr;
  }, [settings?.match, iconInSuffux, searchIcon, loc]);

  return (
    <Input
      {...rest}
      allowClear={allowClear}
      onChange={(e) => {
        rest.onChange?.(e);
        search(e.target.value);
      }}
      placeholder={rest.placeholder ?? loc.word("Search")}
      prefix={!iconInSuffux ? searchIcon : undefined}
      suffix={suffix}
      style={rest.style ?? { width }}
    />
  );
};
